import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { usePortal } from './PortalContext';
import { allowlistRowsToCrmUsers } from '../lib/crmMembers';
import { mergeUsersWithProfile, ownerLabelForRef } from '../utils/ownerId';
import { prefetchClientLogos } from '../lib/clientLogoCache';
import { getActivePortalKind, getDbSupabase, type DbPortalKind } from '../lib/supabase';
import type { AccountTransferRequest, CrmData, CrmNotification, UserFilter, UserRole } from '../types/crm';
import {
  buildOwnerLabelMap,
  collectAssignableOwners,
  detectOwnerIdKind,
  filterMyData,
  type OwnerOption,
  type SalespersonCodesByUsername,
} from '../utils/crmHelpers';
import { formatSalespersonCodes } from '../utils/salespersonScope';
import { fetchActiveSalespersonCodes } from '../lib/applicationFormSettings';
import { refreshMyReminderNotifications } from '../lib/reminderNotifications';
import { markAllNotificationsRead, markNotificationRead } from '../lib/accountTransfer';
import { isOptionalTableError, isTransientLoadError } from '../lib/supabaseOptionalTable';
import { formatSupabaseError } from '../lib/formatSupabaseError';
import type { PostgrestError } from '@supabase/supabase-js';
import { canUseUserFilter as roleCanUseUserFilter, canViewSettings as roleCanViewSettings, canUseViewAs } from '../lib/settingsPermissions';
import { canApproveTransfers as roleCanApproveTransfers, canViewAssignedTeam, canViewAllRecords } from '../lib/roleAccess';
import { canViewAllAccounts, canViewAllAccountsByDepartment } from '../lib/accountVisibility';
import {
  fetchOwnSalespersonCodes,
  fetchPortalUserProfiles,
} from '../lib/portalUserProfiles';
import { normalizeEmail } from '../lib/emailUtils';
import { resolveAuthEmail } from '../lib/authEmail';
import { isSoftPortalEntryPending } from '../lib/portal';
import { useCrmDataRealtime } from '../hooks/useCrmDataRealtime';
import {
  applyCrmSlicePartial,
  CRM_AUXILIARY_SLICES,
  CRM_BLOCKING_SLICES,
  CRM_DEFERRED_CORE_SLICES,
  fetchCrmDataSlices,
  CRM_TASK_PATCH_TTL_MS,
  markTaskDoneInList,
  mergeTaskIntoList,
  mergeTasksFromServer,
  sliceErrorLabel,
  type CrmDataSlice,
  type CrmRefreshOptions,
} from '../lib/crmDataRefresh';
import type { Task } from '../types/crm';

type DataKey = keyof Pick<CrmData, 'clients' | 'contacts' | 'projects' | 'tasks'>;

interface CrmDataContextValue {
  data: CrmData;
  loading: boolean;
  /** True only during the first CRM load after sign-in (not manual Refresh). */
  initialLoading: boolean;
  error: string | null;
  refresh: (options?: CrmRefreshOptions) => Promise<void>;
  /** Merge a saved task into local state without reloading all CRM data. */
  patchTask: (task: Task) => void;
  /** Mark a task Done locally after completeTaskAndMarkVisited. */
  markTaskDoneLocal: (taskId: string) => void;
  getMyData: <K extends DataKey>(key: K) => CrmData[K];
  viewRole: UserRole;
  realRole: UserRole;
  /** Role used for nav, filters, and access checks (viewRole when IT uses View as). */
  effectiveRole: UserRole;
  /** Portal profile department slug (e.g. sales_marketing, finance). */
  departmentSlug: string | null;
  setViewRole: (role: UserRole) => void;
  userFilter: UserFilter;
  setUserFilter: (filter: UserFilter) => void;
  /** @deprecated Visible users / supervisor team removed — always empty. */
  supervisorTeam: string[];
  /** Salesperson codes for account scoping (own, or own+team for supervisor). */
  actorSalespersonCodes: string[];
  /** Own codes only (no team) — used when creating accounts / elevated Me filter. */
  ownSalespersonCodes: string[];
  /** Active codes from User Management master list (`salesperson_emails`). */
  masterSalespersonCodes: string[];
  /** Username → salesperson codes (for Admin User filter / transfers). */
  salespersonCodesByUsername: SalespersonCodesByUsername;
  username: string;
  canUseUserFilter: boolean;
  canViewSettings: boolean;
  canUseViewAs: boolean;
  ownerOptions: OwnerOption[];
  ownerLabel: (ownerRef?: string) => string;
  ownerIdKind: 'uuid' | 'username';
  usersLoadWarning: string | null;
  transferRequests: AccountTransferRequest[];
  notifications: CrmNotification[];
  unreadNotificationCount: number;
  canApproveTransfers: boolean;
  markNotificationRead: (id: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
}

const emptyData: CrmData = {
  users: [],
  clients: [],
  contacts: [],
  projects: [],
  tasks: [],
  updates: [],
  meetingMinutes: [],
  transferRequests: [],
  notifications: [],
  activityLogs: [],
};

const VIEW_ROLES: UserRole[] = ['admin', 'user'];

function viewRoleStorageKey(portalKind: string): string {
  return `bth_view_role_${portalKind}`;
}

function readStoredViewRole(
  portalKind: string,
  realRole: UserRole,
  canUseViewAsRole: boolean,
): UserRole {
  if (!canUseViewAsRole) return realRole;
  try {
    const raw = sessionStorage.getItem(viewRoleStorageKey(portalKind));
    if (raw && VIEW_ROLES.includes(raw as UserRole)) {
      return raw as UserRole;
    }
  } catch {
    /* ignore */
  }
  return 'admin';
}

function writeStoredViewRole(portalKind: string, role: UserRole): void {
  try {
    sessionStorage.setItem(viewRoleStorageKey(portalKind), role);
  } catch {
    /* ignore */
  }
}

const CrmDataContext = createContext<CrmDataContextValue | null>(null);

export function CrmDataProvider({ children }: { children: ReactNode }) {
  const { session, profile, role: realRole, portalAccess } = useAuth();
  const { kind: portalKind } = usePortal();
  const username = profile?.username ?? '';
  const [data, setData] = useState<CrmData>(emptyData);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewRole, setViewRole] = useState<UserRole>(realRole);
  const [userFilter, setUserFilter] = useState<UserFilter>('All');
  const [ownSalespersonCodes, setOwnSalespersonCodes] = useState<string[]>([]);
  const [actorSalespersonCodes, setActorSalespersonCodes] = useState<string[]>([]);
  const [masterSalespersonCodes, setMasterSalespersonCodes] = useState<string[]>([]);
  const [codesByUsername, setCodesByUsername] = useState<SalespersonCodesByUsername>({});
  const refreshGenerationRef = useRef(0);
  const recentTaskPatchesRef = useRef<Map<string, number>>(new Map());
  /** Visible users removed — keep empty for call-site compatibility. */
  const supervisorTeam: string[] = [];

  const canUseViewAsRole = canUseViewAs(realRole, portalAccess?.elevatedDepartment);

  const setViewRoleAndStore = useCallback(
    (role: UserRole) => {
      setViewRole(role);
      if (canUseViewAs(realRole, portalAccess?.elevatedDepartment)) {
        writeStoredViewRole(portalKind, role);
      }
    },
    [portalKind, realRole, portalAccess?.elevatedDepartment],
  );

  useEffect(() => {
    // View as only simulates business roles (IT/Management privilege is by department).
    if (canUseViewAsRole) {
      setViewRole((prev) => {
        const stored = readStoredViewRole(portalKind, realRole, true);
        // Keep intentional admin across realRole it↔admin soft-entry flicker.
        if (prev === 'admin') return prev;
        if (prev === 'user') {
          try {
            const raw = sessionStorage.getItem(viewRoleStorageKey(portalKind));
            // Only keep user when explicitly stored — never from a transient realRole=user.
            if (raw === 'user') return 'user';
          } catch {
            /* ignore */
          }
          return stored;
        }
        // prev was it/management/other (init from realRole) → stored/default admin.
        return stored;
      });
      return;
    }
    // Before profile settles, auth role defaults to "user" — do not bake that into View as.
    if (!profile) return;
    if (realRole === 'user' && (portalAccess?.elevatedDepartment || isSoftPortalEntryPending())) {
      return;
    }
    // Elevation gap on mobile: keep View as admin so we don't toast "User".
    if (realRole === 'user') {
      setViewRole((prev) => (prev === 'admin' ? prev : realRole));
      return;
    }
    setViewRole(realRole);
  }, [realRole, canUseViewAsRole, portalKind, profile, portalAccess?.elevatedDepartment]);

  useEffect(() => {
    if (!session || !username) {
      setOwnSalespersonCodes([]);
      setActorSalespersonCodes([]);
      setMasterSalespersonCodes([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const email =
          normalizeEmail(resolveAuthEmail(session.user) ?? profile?.email ?? '') || '';
        const [ownCodes, profiles, masterCodes] = await Promise.all([
          fetchOwnSalespersonCodes(),
          fetchPortalUserProfiles().catch(() => []),
          fetchActiveSalespersonCodes().catch(() => []),
        ]);
        if (cancelled) return;

        const fromProfile =
          email.length > 0
            ? (profiles.find((p) => normalizeEmail(p.email) === email)?.salesperson_codes ?? [])
            : [];
        const own = formatSalespersonCodes([...ownCodes, ...fromProfile]);
        setOwnSalespersonCodes(own);
        setActorSalespersonCodes(own);
        setMasterSalespersonCodes(masterCodes);
      } catch {
        // Keep last known codes on transient failure — do not wipe assignments.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, username, portalKind, profile?.email]);

  const isSliceSoftFailure = useCallback((slice: CrmDataSlice, err: PostgrestError | null) => {
    if (!err) return false;
    const label = sliceErrorLabel(slice);
    if (CRM_AUXILIARY_SLICES.includes(slice)) {
      return isOptionalTableError(err, label) || isTransientLoadError(err);
    }
    if (slice === 'transferRequests' || slice === 'notifications') {
      return isOptionalTableError(err, label) || isTransientLoadError(err);
    }
    return false;
  }, []);

  const touchRecentTaskPatch = useCallback((taskId: string) => {
    recentTaskPatchesRef.current.set(String(taskId), Date.now());
  }, []);

  const recentTaskPatchIds = useCallback((): Set<string> => {
    const now = Date.now();
    const keep = new Set<string>();
    for (const [id, touchedAt] of recentTaskPatchesRef.current.entries()) {
      if (now - touchedAt < CRM_TASK_PATCH_TTL_MS) {
        keep.add(id);
      } else {
        recentTaskPatchesRef.current.delete(id);
      }
    }
    return keep;
  }, []);

  const applySliceResults = useCallback(
    (slices: CrmDataSlice[], result: Awaited<ReturnType<typeof fetchCrmDataSlices>>) => {
      const hardFailure = slices.find((slice) => {
        const err = result.errors[slice];
        return err && !isSliceSoftFailure(slice, err);
      });
      if (hardFailure) {
        const err = result.errors[hardFailure]!;
        throw new Error(`Failed to load ${sliceErrorLabel(hardFailure)}: ${formatSupabaseError(err)}`);
      }

      const teamFromAllowlist = result.rawUsers ? allowlistRowsToCrmUsers(result.rawUsers) : null;

      setData((prev) => {
        let next = applyCrmSlicePartial(prev, result.partial);
        if (teamFromAllowlist) {
          next = { ...next, users: teamFromAllowlist };
        }
        if (result.partial.tasks !== undefined && slices.includes('tasks')) {
          next = {
            ...next,
            tasks: mergeTasksFromServer(prev.tasks, result.partial.tasks, recentTaskPatchIds()),
          };
        }
        if (slices.includes('meetingMinutes') && isSliceSoftFailure('meetingMinutes', result.errors.meetingMinutes ?? null)) {
          next = { ...next, meetingMinutes: prev.meetingMinutes };
        }
        if (slices.includes('activityLogs') && isSliceSoftFailure('activityLogs', result.errors.activityLogs ?? null)) {
          next = { ...next, activityLogs: prev.activityLogs };
        }
        if (
          slices.includes('transferRequests') &&
          isSliceSoftFailure('transferRequests', result.errors.transferRequests ?? null)
        ) {
          next = { ...next, transferRequests: prev.transferRequests };
        }
        if (
          slices.includes('notifications') &&
          isSliceSoftFailure('notifications', result.errors.notifications ?? null)
        ) {
          next = { ...next, notifications: prev.notifications };
        }
        return next;
      });

      if (result.partial.clients) {
        void prefetchClientLogos(result.partial.clients.map((c) => c.logo_path));
      }

      return teamFromAllowlist;
    },
    [isSliceSoftFailure, recentTaskPatchIds],
  );

  const syncTeamSalespersonCodes = useCallback(
    async (teamFromAllowlist: ReturnType<typeof allowlistRowsToCrmUsers> | null) => {
      if (!session || !teamFromAllowlist) return;
      try {
        const profiles = await fetchPortalUserProfiles();
        const byEmail = new Map(
          profiles.map((p) => [normalizeEmail(p.email), formatSalespersonCodes(p.salesperson_codes ?? [])]),
        );
        const map: SalespersonCodesByUsername = {};
        for (const u of teamFromAllowlist) {
          const email = u.email ? normalizeEmail(u.email) : '';
          const codes = email ? byEmail.get(email) ?? [] : [];
          if (u.username) {
            map[u.username] = codes;
            map[u.username.trim().toLowerCase()] = codes;
          }
        }
        setCodesByUsername(map);

        const myEmail = normalizeEmail(resolveAuthEmail(session.user) ?? profile?.email ?? '');
        const fromProfile = myEmail ? byEmail.get(myEmail) ?? [] : [];
        if (fromProfile.length > 0) {
          setOwnSalespersonCodes((prev) => formatSalespersonCodes([...prev, ...fromProfile]));
          setActorSalespersonCodes((prev) => formatSalespersonCodes([...prev, ...fromProfile]));
        }
      } catch {
        setCodesByUsername({});
      }
    },
    [session, profile?.email],
  );

  const refreshRemindersIfNeeded = useCallback(async () => {
    const remindersRan = await refreshMyReminderNotifications();
    if (!remindersRan || !profile?.id) return;
    const { data: freshNotifications, error: notificationsError } = await getDbSupabase()
      .from('crm_notifications')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(80);
    if (!notificationsError && freshNotifications) {
      setData((prev) => ({ ...prev, notifications: freshNotifications }));
    }
  }, [profile?.id]);

  const loadSlices = useCallback(
    async (slices: CrmDataSlice[], generation: number) => {
      const db = getDbSupabase();
      const result = await fetchCrmDataSlices(db, slices, profile?.id);
      if (generation !== refreshGenerationRef.current) return null;
      return applySliceResults(slices, result);
    },
    [applySliceResults, profile?.id],
  );

  const refresh = useCallback(
    async (options?: CrmRefreshOptions) => {
      if (!session) {
        setData(emptyData);
        setHasLoaded(false);
        setLoading(false);
        return;
      }

      const silent = options?.silent ?? false;
      const partialSlices = options?.slices;
      const isPartial = Boolean(partialSlices?.length);
      const generation = isPartial ? refreshGenerationRef.current : ++refreshGenerationRef.current;

      if (!silent && !isPartial) {
        setLoading(true);
      }
      if (!silent || !isPartial) {
        setError(null);
      }

      try {
        if (isPartial && partialSlices) {
          await loadSlices(partialSlices, generation);
          return;
        }

        const teamFromAllowlist = await loadSlices(CRM_BLOCKING_SLICES, generation);
        if (generation !== refreshGenerationRef.current) return;
        if (!silent) {
          setLoading(false);
        }

        void (async () => {
          try {
            const bgGen = generation;
            await loadSlices(CRM_DEFERRED_CORE_SLICES, bgGen);
            await loadSlices(CRM_AUXILIARY_SLICES, bgGen);
            if (bgGen !== refreshGenerationRef.current) return;
            await syncTeamSalespersonCodes(teamFromAllowlist);
            void fetchActiveSalespersonCodes()
              .then((codes) => setMasterSalespersonCodes(codes))
              .catch(() => {
                /* keep last known master list */
              });
            await refreshRemindersIfNeeded();
          } catch {
            /* background enrichment — never block the UI */
          }
        })();
      } catch (e) {
        if (!silent) {
          setError(e instanceof Error ? e.message : formatSupabaseError(e));
        }
      } finally {
        if (generation === refreshGenerationRef.current) {
          setHasLoaded(true);
          if (!silent && !isPartial) {
            setLoading(false);
          }
        }
      }
    },
    [session, loadSlices, syncTeamSalespersonCodes, refreshRemindersIfNeeded],
  );

  const patchTask = useCallback(
    (task: Task) => {
      touchRecentTaskPatch(String(task.id));
      setData((prev) => ({ ...prev, tasks: mergeTaskIntoList(prev.tasks, task) }));
    },
    [touchRecentTaskPatch],
  );

  const markTaskDoneLocal = useCallback(
    (taskId: string) => {
      touchRecentTaskPatch(taskId);
      setData((prev) => ({ ...prev, tasks: markTaskDoneInList(prev.tasks, taskId) }));
    },
    [touchRecentTaskPatch],
  );

  useEffect(() => {
    if (session) {
      void refresh();
    } else {
      setData(emptyData);
    }
  }, [session, refresh, portalKind]);

  const dbPortalKind: DbPortalKind = portalKind === 'it' ? 'it' : 'crm';

  useCrmDataRealtime({
    enabled: Boolean(session),
    portalKind: dbPortalKind,
    onChange: (slices) => {
      void refresh({ silent: true, slices });
    },
  });

  useEffect(() => {
    if (!session || !profile?.id) return;

    const schema = getActivePortalKind();
    const channel = getDbSupabase()
      .channel(`crm-notifications:${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema,
          table: 'crm_notifications',
          filter: `user_id=eq.${profile.id}`,
        },
        () => {
          void refresh({ silent: true, slices: ['notifications'] });
        },
      )
      .subscribe();

    return () => {
      void getDbSupabase().removeChannel(channel);
    };
  }, [session, profile?.id, refresh, portalKind]);

  const teamUsers = useMemo(
    () => mergeUsersWithProfile(data.users, profile),
    [data.users, profile],
  );

  const dataWithTeam = useMemo(() => ({ ...data, users: teamUsers }), [data, teamUsers]);

  /** IT “View as” simulates another role in UI and data filters. */
  const effectiveRole = canUseViewAsRole ? viewRole : realRole;
  const departmentSlug = portalAccess?.departmentSlug ?? null;
  // When IT Views as user/supervisor, hide department-wide account browse so scope matches the simulated role.
  const departmentSlugForFilter =
    canUseViewAsRole && viewRole !== realRole && !canViewAllRecords(viewRole)
      ? null
      : departmentSlug;

  const getMyData = useCallback(
    <K extends DataKey>(key: K): CrmData[K] => {
      const roleForFilter = canUseViewAs(realRole, portalAccess?.elevatedDepartment)
        ? viewRole
        : realRole;
      const teamForFilter = canViewAssignedTeam(roleForFilter) ? supervisorTeam : [];
      const browseAllAccounts = canViewAllAccounts(roleForFilter, departmentSlugForFilter);
      // Elevated Me filter uses own codes only; list scope for sales uses actor codes.
      const codesForFilter = browseAllAccounts ? ownSalespersonCodes : actorSalespersonCodes;
      return filterMyData(
        key,
        dataWithTeam,
        roleForFilter,
        username,
        userFilter,
        teamForFilter,
        codesForFilter,
        codesByUsername,
        departmentSlugForFilter,
      ) as CrmData[K];
    },
    [
      dataWithTeam,
      viewRole,
      realRole,
      username,
      userFilter,
      supervisorTeam,
      portalAccess?.elevatedDepartment,
      departmentSlugForFilter,
      ownSalespersonCodes,
      actorSalespersonCodes,
      codesByUsername,
    ],
  );
  const canUseUserFilter =
    roleCanUseUserFilter(effectiveRole) || canViewAllAccountsByDepartment(departmentSlugForFilter);
  const canViewSettings = roleCanViewSettings(effectiveRole);
  const canApproveTransfers = roleCanApproveTransfers(effectiveRole);

  const unreadNotificationCount = useMemo(
    () => data.notifications.filter((n) => !n.read_at).length,
    [data.notifications],
  );

  const handleMarkNotificationRead = useCallback(
    async (id: string) => {
      await markNotificationRead(id);
      setData((prev) => ({
        ...prev,
        notifications: prev.notifications.map((n) =>
          n.id === id ? { ...n, read_at: n.read_at ?? new Date().toISOString() } : n,
        ),
      }));
    },
    [],
  );

  const handleMarkAllNotificationsRead = useCallback(async () => {
    if (!profile?.id) return;
    await markAllNotificationsRead(profile.id);
    const now = new Date().toISOString();
    setData((prev) => ({
      ...prev,
      notifications: prev.notifications.map((n) => ({ ...n, read_at: n.read_at ?? now })),
    }));
  }, [profile?.id]);

  const ownerIdKind = useMemo(() => detectOwnerIdKind(teamUsers, data), [teamUsers, data]);
  const ownerOptions = useMemo(
    () => collectAssignableOwners(teamUsers, ownerIdKind),
    [teamUsers, ownerIdKind],
  );
  const ownerLabelMap = useMemo(() => buildOwnerLabelMap(teamUsers, data), [teamUsers, data]);

  const ownerLabel = useCallback(
    (ownerRef?: string) => {
      if (!ownerRef) return '—';
      return ownerLabelMap.get(ownerRef) ?? ownerLabelForRef(teamUsers, ownerRef);
    },
    [ownerLabelMap, teamUsers],
  );

  const usersLoadWarning = useMemo(() => {
    // Avoid false “only your user” when fetch failed or has not finished.
    if (loading || !session || !hasLoaded || error) return null;
    if (teamUsers.length === 0) {
      return roleCanViewSettings(realRole)
        ? 'No users loaded. Add emails in Settings (or run docs/SETTINGS-ALLOWED-EMAILS-zh.md) and sign in again.'
        : 'No users loaded. Ask an admin or IT to add your email in Settings.';
    }
    // data.users is the CRM allowlist fetch; teamUsers may still include the signed-in profile.
    if (data.users.length <= 1 && profile) {
      // One row is normal if you truly only have yourself on the CRM allowlist.
      // Only warn when allowlist is empty and we synthesized the current user.
      if (data.users.length === 0) {
        return roleCanViewSettings(realRole)
          ? 'CRM team list is empty in this workspace. Check Settings → User management (CRM access), then refresh.'
          : 'CRM team list is empty. Ask an admin or IT to add colleagues in Settings.';
      }
    }
    return null;
  }, [loading, session, hasLoaded, error, teamUsers.length, data.users.length, profile, realRole]);

  const value = useMemo(
    () => ({
      data,
      loading,
      initialLoading: loading && !hasLoaded,
      error,
      refresh,
      patchTask,
      markTaskDoneLocal,
      getMyData,
      viewRole,
      realRole,
      effectiveRole,
      departmentSlug,
      setViewRole: setViewRoleAndStore,
      userFilter,
      setUserFilter,
      supervisorTeam,
      actorSalespersonCodes,
      ownSalespersonCodes,
      masterSalespersonCodes,
      salespersonCodesByUsername: codesByUsername,
      username,
      canUseUserFilter,
      canViewSettings,
      canUseViewAs: canUseViewAsRole,
      ownerOptions,
      ownerLabel,
      ownerIdKind,
      usersLoadWarning,
      transferRequests: data.transferRequests,
      notifications: data.notifications,
      unreadNotificationCount,
      canApproveTransfers,
      markNotificationRead: handleMarkNotificationRead,
      markAllNotificationsRead: handleMarkAllNotificationsRead,
    }),
    [
      data,
      loading,
      hasLoaded,
      error,
      refresh,
      patchTask,
      markTaskDoneLocal,
      getMyData,
      viewRole,
      realRole,
      effectiveRole,
      departmentSlug,
      setViewRoleAndStore,
      userFilter,
      supervisorTeam,
      actorSalespersonCodes,
      ownSalespersonCodes,
      masterSalespersonCodes,
      codesByUsername,
      username,
      canUseUserFilter,
      canViewSettings,
      canUseViewAsRole,
      ownerOptions,
      ownerLabel,
      ownerIdKind,
      usersLoadWarning,
      unreadNotificationCount,
      canApproveTransfers,
      handleMarkNotificationRead,
      handleMarkAllNotificationsRead,
    ],
  );

  return <CrmDataContext.Provider value={value}>{children}</CrmDataContext.Provider>;
}

export function useCrmData(): CrmDataContextValue {
  const ctx = useContext(CrmDataContext);
  if (!ctx) {
    throw new Error('useCrmData must be used within CrmDataProvider');
  }
  return ctx;
}

export function useOptionalCrmData(): CrmDataContextValue | null {
  return useContext(CrmDataContext);
}
